import { useState } from 'react';
import { designDocumentsApi } from '../../../services/api';
import { api } from '../../../services/api';
import type { ProjectInstance, DesignDocument } from './types';
import { 
  Button, 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogFooter, 
  DialogTitle, 
  DialogDescription,
  Input,
  Label,
  RadixSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';
import { logger } from '../../../utils/logger';

interface DocumentsTabProps {
  project: ProjectInstance;
  onOpenDocument: (doc: {
    id: string;
    documentNumber?: string;
    projectId?: string;
    title: string;
    type: 'project' | 'company' | 'standard' | 'prior-project';
    canEdit?: boolean;
    status?: string;
    currentRevision?: string;
  }) => void;
  onRefreshProject?: () => void;
}

interface DocumentRowDoc {
  documentNumber: string;
  status: string;
  projectSpecificTitle?: string;
  documentTitle?: string;
  modifiedDate?: string;
  aiStatus?: string;
  description?: string;
  id?: string;
  currentRevision?: string;
  revisions?: Array<{ revision: string; revisionDate?: string; status?: string; createdAt?: string; createdBy?: string }>;
}

export function DocumentsTab({ project, onOpenDocument, onRefreshProject }: DocumentsTabProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [docsSubTab, setDocsSubTab] = useState<'certification' | 'design'>('certification');
  const [expandedDocuments, setExpandedDocuments] = useState<Set<string>>(new Set());
  
  const [designDocs, setDesignDocs] = useState<DesignDocument[]>([]);
  const [loadingDesign, setLoadingDesign] = useState(false);
  const [hasLoadedDesign, setHasLoadedDesign] = useState(false);

  const [showCreateDocModal, setShowCreateDocModal] = useState(false);
  const [showDeleteDocModal, setShowDeleteDocModal] = useState(false);
  const [docToDelete, setDocToDelete] = useState<DocumentRowDoc | null>(null);
  const [newDocNumber, setNewDocNumber] = useState('');
  const [newDocTitle, setNewDocTitle] = useState('');
  const [newDocType, setNewDocType] = useState('');
  const [newDocRevision, setNewDocRevision] = useState('IR');
  const [isCreatingDoc, setIsCreatingDoc] = useState(false);
  const [isDeletingDoc, setIsDeletingDoc] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const loadDesignDocuments = async () => {
    setLoadingDesign(true);
    try {
      const allDesignDocs = await designDocumentsApi.list();
      const projectDesignDocs = allDesignDocs.filter((doc: DesignDocument) => 
        doc.projectId === project.id || (doc.usedInProjects && doc.usedInProjects.includes(project.id))
      );
      setDesignDocs(projectDesignDocs);
      setHasLoadedDesign(true);
    } catch (err) {
      logger.error('Failed to load design documents:', err);
    } finally {
      setLoadingDesign(false);
    }
  };

  if (docsSubTab === 'design' && !hasLoadedDesign && !loadingDesign) {
    loadDesignDocuments();
  }

  const getFilteredDocuments = () => {
    if (!project.documents) return [];
    
    let filtered = [...project.documents];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.documentNumber.toLowerCase().includes(term) ||
        doc.documentTitle.toLowerCase().includes(term) ||
        (doc.projectSpecificTitle || '').toLowerCase().includes(term)
      );
    }
    
    if (statusFilter) {
      filtered = filtered.filter(doc => doc.status === statusFilter);
    }
    
    filtered.sort((a, b) => a.documentNumber.localeCompare(b.documentNumber));
    
    return filtered;
  };

  const handleDocumentClick = (doc: { documentNumber: string; status: string; projectSpecificTitle?: string; documentTitle?: string; currentRevision?: string }) => {
    const canEdit = doc.status === 'Draft' || doc.status === 'In Review';
    onOpenDocument({
      id: doc.documentNumber,
      documentNumber: doc.documentNumber,
      projectId: project.id,
      title: doc.projectSpecificTitle || doc.documentTitle,
      type: 'project',
      canEdit,
      status: doc.status,
      currentRevision: doc.currentRevision,
    });
  };

  const generateDocNumber = (docType: string) => {
    const code = project.projectCode || 'PROJ';
    const typePrefix = docType.toUpperCase().replace(/\s+/g, '-').substring(0, 8);
    const existingCount = project.documents?.filter(d => 
      d.documentNumber.includes(typePrefix)
    ).length || 0;
    return `${code}-${typePrefix}-${String(existingCount + 1).padStart(3, '0')}`;
  };

  const handleCreateDocument = async () => {
    if (!newDocTitle.trim() || !newDocType) return;
    
    setIsCreatingDoc(true);
    try {
      const docNumber = newDocNumber || generateDocNumber(newDocType);
      await api.post(`/projects/${project.id}/documents`, {
        documentNumber: docNumber,
        documentTitle: newDocTitle.trim(),
        documentType: newDocType,
        projectSpecificTitle: newDocTitle.trim(),
        status: 'Draft',
        revision: newDocRevision,
        currentRevision: newDocRevision
      });
      
      setShowCreateDocModal(false);
      setNewDocNumber('');
      setNewDocTitle('');
      setNewDocType('');
      setNewDocRevision('IR');
      onRefreshProject?.();
    } catch (err) {
      logger.error('Failed to create document:', err);
    } finally {
      setIsCreatingDoc(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!docToDelete || deleteConfirmText.toLowerCase() !== 'delete') return;
    
    setIsDeletingDoc(true);
    try {
      await api.delete(`/projects/${project.id}/documents/${docToDelete.documentNumber}`);
      
      setShowDeleteDocModal(false);
      setDocToDelete(null);
      setDeleteConfirmText('');
      onRefreshProject?.();
    } catch (err) {
      logger.error('Failed to delete document:', err);
    } finally {
      setIsDeletingDoc(false);
    }
  };

  const openDeleteDocModal = (doc: DocumentRowDoc, e: React.MouseEvent) => {
    e.stopPropagation();
    setDocToDelete(doc);
    setDeleteConfirmText('');
    setShowDeleteDocModal(true);
  };

  return (
    <div className="space-y-4">
      {/* Header with sub-tabs and create button */}
      <div className="flex items-center justify-between">
        {/* Sub-tabs */}
        <div className="inline-flex gap-1 bg-muted p-1 rounded-lg">
        <Button
          onClick={() => setDocsSubTab('certification')}
          variant="ghost"
          size="sm"
          className={`py-1.5 px-3 ${
            docsSubTab === 'certification'
              ? 'bg-card text-link font-medium shadow-sm'
              : 'text-muted-foreground'
          }`}
        >
          📋 Certification ({project.documents?.length || 0})
        </Button>
        <Button
          onClick={() => setDocsSubTab('design')}
          variant="ghost"
          size="sm"
          className={`py-1.5 px-3 ${
            docsSubTab === 'design'
              ? 'bg-card text-link font-medium shadow-sm'
              : 'text-muted-foreground'
          }`}
        >
          📐 Design ({designDocs.length})
        </Button>
        </div>
        
        {/* Create Document button */}
        {docsSubTab === 'certification' && (
          <Button
            onClick={() => setShowCreateDocModal(true)}
            size="sm"
          >
            + Create Document
          </Button>
        )}
      </div>
      
      {/* Certification Documents */}
      {docsSubTab === 'certification' && (
        <>
          {/* Search and filter */}
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            {/* eslint-disable-next-line no-restricted-syntax -- Native select for status filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-border rounded-lg bg-card text-foreground"
            >
              <option value="">All Status</option>
              <option value="Draft">Draft</option>
              <option value="In Review">In Review</option>
              <option value="Approved">Approved</option>
              <option value="Released">Released</option>
            </select>
          </div>
          
          {/* Document list */}
          {(!project.documents || project.documents.length === 0) ? (
            <div className="text-center py-8 text-muted-foreground">
              <div className="text-4xl mb-3">📄</div>
              <p>No certification documents yet</p>
            </div>
          ) : getFilteredDocuments().length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No documents match your filters</p>
            </div>
          ) : (
            <div className="space-y-2">
              {getFilteredDocuments().map(doc => (
                <DocumentRow
                  key={doc.documentNumber}
                  doc={doc}
                  isExpanded={expandedDocuments.has(doc.documentNumber)}
                  onToggleExpand={() => {
                    const newExpanded = new Set(expandedDocuments);
                    if (newExpanded.has(doc.documentNumber)) {
                      newExpanded.delete(doc.documentNumber);
                    } else {
                      newExpanded.add(doc.documentNumber);
                    }
                    setExpandedDocuments(newExpanded);
                  }}
                  onClick={() => handleDocumentClick(doc)}
                  onDelete={(e) => openDeleteDocModal(doc, e)}
                />
              ))}
            </div>
          )}
        </>
      )}
      
      {/* Design Documents */}
      {docsSubTab === 'design' && (
        <>
          {loadingDesign ? (
            <div className="text-center py-8">
              <div className="animate-spin text-2xl">⏳</div>
            </div>
          ) : designDocs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <div className="text-4xl mb-3">📐</div>
              <p>No design documents registered</p>
              <p className="text-sm mt-1">
                CAD drawings, schematics, and other design artifacts
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {designDocs.map(doc => (
                <div
                  key={doc.documentNumber}
                  className="p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-foreground">
                        {doc.documentNumber}
                      </span>
                      <p className="text-xs text-muted-foreground truncate">
                        {doc.documentTitle}
                      </p>
                      {doc.partNumber && (
                        <p className="text-xs text-muted-foreground">
                          P/N: {doc.partNumber}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-0.5 bg-info-light text-info rounded">
                        {doc.documentType}-{doc.discipline}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        doc.scope === 'shared'
                          ? 'bg-success-light text-success'
                          : 'bg-muted text-foreground'
                      }`}>
                        {doc.scope === 'shared' ? '🔗 Shared' : '📁 Project'}
                      </span>
                    </div>
                  </div>
                  {doc.externalStorage?.fileUrl && (
                    <a
                      href={doc.externalStorage.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-xs text-info hover:underline"
                    >
                      Open external file →
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create Document Modal */}
      <Dialog open={showCreateDocModal} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDocModal(false);
          setNewDocNumber('');
          setNewDocTitle('');
          setNewDocType('');
          setNewDocRevision('IR');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Document</DialogTitle>
            <DialogDescription>
              Create a new certification document for this project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="doc-type">Document Type *</Label>
              <RadixSelect
                value={newDocType}
                onValueChange={(value) => {
                  setNewDocType(value);
                  setNewDocNumber(generateDocNumber(value));
                }}
              >
                <SelectTrigger id="doc-type">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PSAC">PSAC - Software Accomplishment Summary</SelectItem>
                  <SelectItem value="SDP">SDP - Software Development Plan</SelectItem>
                  <SelectItem value="SVP">SVP - Software Verification Plan</SelectItem>
                  <SelectItem value="SCMP">SCMP - Software Configuration Management Plan</SelectItem>
                  <SelectItem value="SQAP">SQAP - Software Quality Assurance Plan</SelectItem>
                  <SelectItem value="SRS">SRS - Software Requirements Specification</SelectItem>
                  <SelectItem value="SDD">SDD - Software Design Description</SelectItem>
                  <SelectItem value="SVR">SVR - Software Verification Results</SelectItem>
                  <SelectItem value="OTHER">Other Document</SelectItem>
                </SelectContent>
              </RadixSelect>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="doc-number">Document Number</Label>
              <Input
                id="doc-number"
                value={newDocNumber}
                onChange={(e) => setNewDocNumber(e.target.value)}
                placeholder="Auto-generated based on type"
                className="font-mono"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="doc-revision">Initial Revision</Label>
              <RadixSelect
                value={newDocRevision}
                onValueChange={setNewDocRevision}
              >
                <SelectTrigger id="doc-revision">
                  <SelectValue placeholder="Select revision..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IR">IR - Initial Release</SelectItem>
                  {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((letter) => (
                    <SelectItem key={letter} value={letter}>{letter}</SelectItem>
                  ))}
                </SelectContent>
              </RadixSelect>
              <p className="text-xs text-muted-foreground">
                IR = Initial Release (default). Use a letter revision when importing an existing document.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="doc-title">Document Title *</Label>
              <Input
                id="doc-title"
                value={newDocTitle}
                onChange={(e) => setNewDocTitle(e.target.value)}
                placeholder="e.g., Navigation Display Software Development Plan"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setShowCreateDocModal(false);
                setNewDocNumber('');
                setNewDocTitle('');
                setNewDocType('');
                setNewDocRevision('IR');
              }}
              variant="outline"
              disabled={isCreatingDoc}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateDocument}
              disabled={!newDocTitle.trim() || !newDocType || isCreatingDoc}
            >
              {isCreatingDoc ? 'Creating...' : 'Create Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Document Modal */}
      <Dialog open={showDeleteDocModal && !!docToDelete} onOpenChange={(open) => {
        if (!open) {
          setShowDeleteDocModal(false);
          setDocToDelete(null);
          setDeleteConfirmText('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this document? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {docToDelete && (
            <div className="space-y-4 py-4">
              <div className="bg-muted/30 rounded-lg p-3 text-sm space-y-2">
                <div>
                  <strong>Document:</strong> {docToDelete.documentNumber}
                </div>
                <div>
                  <strong>Title:</strong> {docToDelete.projectSpecificTitle || docToDelete.documentTitle}
                </div>
                <div>
                  <strong>Status:</strong> {docToDelete.status}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="delete-confirm">Type "delete" to confirm</Label>
                <Input
                  id="delete-confirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="delete"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => {
                setShowDeleteDocModal(false);
                setDocToDelete(null);
                setDeleteConfirmText('');
              }}
              variant="outline"
              disabled={isDeletingDoc}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteDocument}
              disabled={deleteConfirmText.toLowerCase() !== 'delete' || isDeletingDoc}
              variant="destructive"
            >
              {isDeletingDoc ? 'Deleting...' : 'Delete Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DocumentRow({ doc, isExpanded, onToggleExpand, onClick, onDelete }: {
  doc: DocumentRowDoc;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClick: () => void;
  onDelete?: (e: React.MouseEvent) => void;
}) {
  return (
    <div className="overflow-hidden">
      <div
        className="p-3 cursor-pointer hover:bg-muted/50 transition-colors rounded-lg"
        onClick={onClick}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-info">
                {doc.documentNumber}
              </span>
              <span className="text-xs text-muted-foreground">
                Rev {doc.currentRevision}
              </span>
            </div>
            <p className="text-xs text-muted-foreground truncate">
              {doc.projectSpecificTitle || doc.documentTitle}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {doc.status === 'Draft' && onDelete && (
              <Button
                onClick={onDelete}
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-destructive"
                title="Delete draft document"
              >
                <span className="text-sm">🗑️</span>
              </Button>
            )}
            <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded ${
              doc.status === 'Released' ? 'bg-success-light text-success' :
              doc.status === 'Approved' ? 'bg-info-light text-info' :
              doc.status === 'In Review' ? 'bg-secondary text-secondary-foreground' :
              'bg-warning-light text-warning'
            }`}>
              {doc.status}
            </span>
            {doc.revisions && doc.revisions.length > 1 && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand();
                }}
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground"
                title={`${doc.revisions.length} revisions`}
              >
                {isExpanded ? '▼' : '▶'}
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {isExpanded && doc.revisions && (
        <div className="px-3 pb-3 pt-1 border-t border-border">
          <div className="text-xs font-medium text-muted-foreground mb-2">
            Revision History
          </div>
          <div className="space-y-1">
            {doc.revisions.slice().reverse().map((rev) => (
              <div
                key={rev.revision}
                className="flex items-center justify-between text-xs p-1.5 bg-muted rounded"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">{rev.revision}</span>
                  <span className="text-muted-foreground">
                    {rev.revisionDate ? new Date(rev.revisionDate).toLocaleDateString() : ''}
                  </span>
                </div>
                <span className={`px-1.5 py-0.5 rounded ${
                  rev.status === 'Released' ? 'bg-success-light text-success' :
                  rev.status === 'Superseded' ? 'bg-muted text-muted-foreground' :
                  'bg-warning-light text-warning'
                }`}>
                  {rev.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
